import type { ApiResponse, AuthResponse, Site, Payment, PlanTier, User } from '@wranglr/shared';

const API_BASE = 'https://api.wranglr.co.za/api';

async function request<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<ApiResponse<T>> {
    const token = localStorage.getItem('token');

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
    };

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        return { 
            success: false, 
            error: `Server Error: ${response.status} ${response.statusText}`, 
            message: 'The server took too long to respond or crashed.' 
        } as any;
    }
    
    return data;
}

// Auth
export async function login(email: string, password: string): Promise<ApiResponse<AuthResponse>> {
    return request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
    });
}

export async function register(email: string, password: string): Promise<ApiResponse<AuthResponse>> {
    return request<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password })
    });
}

export async function forgotPassword(email: string): Promise<ApiResponse<{ message: string }>> {
    return request('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
    });
}

export async function resetPassword(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
    return request('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password })
    });
}

export async function getMe(): Promise<ApiResponse<{ user: Omit<User, 'password_hash'> }>> {
    return request('/auth/me');
}

// Sites
export async function getSites(): Promise<ApiResponse<Site[]>> {
    return request<Site[]>('/sites');
}

export async function createSite(domain: string): Promise<ApiResponse<Site & { dnsInstructions: { type: string; name: string; target: string; message: string } }>> {
    return request('/sites', {
        method: 'POST',
        body: JSON.stringify({ domain })
    });
}

export async function deleteSite(siteId: string): Promise<ApiResponse<{ message: string }>> {
    return request(`/sites/${siteId}`, { method: 'DELETE' });
}

export async function purgeCache(siteId: string): Promise<ApiResponse<{ message: string }>> {
    return request(`/sites/${siteId}/purge-cache`, { method: 'POST' });
}

export async function checkDnsStatus(siteId: string): Promise<ApiResponse<{
    status: 'not_configured' | 'propagating' | 'propagated';
    message: string;
    domain: string;
    expectedTarget: string;
    propagated: boolean;
    configuredCount: number;
    totalResolvers: number;
    resolvers: Array<{ resolver: string; configured: boolean; record: string | null }>;
}>> {
    return request(`/sites/${siteId}/dns-status`);
}

export async function updateSiteDomain(siteId: string, domain: string): Promise<ApiResponse<any>> {
    return request(`/sites/${siteId}/domain`, {
        method: 'PUT',
        body: JSON.stringify({ domain })
    });
}

// Files
export async function getFiles(siteId: string): Promise<ApiResponse<{ name: string; size: number; lastModified: string }[]>> {
    return request(`/upload/files/${siteId}`);
}

export async function getPresignedUrl(siteId: string, filename: string, contentType?: string): Promise<ApiResponse<{ objectKey: string; uploadUrl: string; contentType: string }>> {
    return request('/upload/presign', {
        method: 'POST',
        body: JSON.stringify({ siteId, filename, contentType })
    });
}

export async function deleteFile(siteId: string, filename: string): Promise<ApiResponse<{ message: string }>> {
    return request(`/upload/file/${siteId}/${encodeURIComponent(filename)}`, { method: 'DELETE' });
}

export async function deleteAllFiles(siteId: string): Promise<ApiResponse<{ message: string }>> {
    return request(`/upload/all/${siteId}`, { method: 'DELETE' });
}

export async function getFileContent(siteId: string, filename: string): Promise<string> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}/upload/content/${siteId}/${encodeURIComponent(filename)}`, {
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
    });

    if (!response.ok) {
        throw new Error('Failed to load file content');
    }

    return response.text();
}

export async function saveFileContent(siteId: string, filename: string, content: string): Promise<ApiResponse<any>> {
    const token = localStorage.getItem('token');
    
    // We do NOT send Content-Type: text/plain here, 
    // otherwise the API worker will save HTML files as text/plain,
    // which breaks the website and causes 1-year cache headers!
    const response = await fetch(`${API_BASE}/upload/direct/${siteId}/${encodeURIComponent(filename)}`, {
        method: 'POST',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: content
    });

    let data;
    try {
        data = await response.json();
    } catch (err) {
        return { success: false, error: 'Server error saving file' } as any;
    }
    return data;
}

export async function uploadStoreAsset(siteId: string, filename: string, file: File): Promise<ApiResponse<{ objectKey: string, size: number, contentType: string }>> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}/upload/direct/${siteId}/${encodeURIComponent(filename)}`, {
        method: 'POST',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: file
    });
    try {
        return await response.json() as any;
    } catch (err) {
        return { success: false, error: 'Server error uploading asset' } as any;
    }
}

// Payments
export async function createCheckout(plan: string, type: 'hosting' | 'store_license' = 'hosting'): Promise<ApiResponse<{ checkoutId: string; redirectUrl: string }>> {
    return request('/payments/checkout', {
        method: 'POST',
        body: JSON.stringify({ plan, type })
    });
}

export async function getPaymentHistory(): Promise<ApiResponse<Payment[]>> {
    return request<Payment[]>('/payments/history');
}

// --- Admin ---
export async function updateUserCustomPlan(userId: string, data: {
    price_cents: number;
    max_sites: number;
    max_storage: number;
}): Promise<ApiResponse<any>> {
    return request(`/admin/users/${userId}/custom-plan`, {
        method: 'PUT',
        body: JSON.stringify(data)
    });
}

// --- Messages / Contact ---
export interface ContactThread {
    id: string;
    user_id: string;
    email: string;
    name?: string | null;
    subject: string;
    status: 'open' | 'replied' | 'closed';
    created_at: string;
    updated_at: string;
    messages?: ContactMessage[];
    admin_unread?: number;
}

export interface ContactMessage {
    id: string;
    thread_id: string;
    sender_type: 'user' | 'admin';
    message: string;
    created_at: string;
}

export async function getContactThreads(admin: boolean = false): Promise<ApiResponse<ContactThread[]>> {
    return request<ContactThread[]>(admin ? '/admin/messages' : '/contact/threads');
}

export async function createContactThread(subject: string, message: string): Promise<ApiResponse<ContactThread>> {
    return request('/contact/threads', {
        method: 'POST',
        body: JSON.stringify({ subject, message })
    });
}

export async function replyToThread(threadId: string, message: string, admin: boolean = false): Promise<ApiResponse<ContactMessage>> {
    return request(admin ? `/admin/messages/${threadId}/reply` : `/contact/threads/${threadId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message })
    });
}

// --- A/B Testing ---
export interface ABTest {
    id: string;
    site_id: string;
    path: string;
    variant_a_path: string;
    variant_b_path: string;
    status: 'active' | 'paused' | 'stopped';
    created_at: string;
    traffic_split: number; // 0-1 (e.g. 0.5 for 50/50)
    stats?: {
        views_a: number;
        views_b: number;
    };
}

export async function getABTests(siteId: string): Promise<ApiResponse<ABTest[]>> {
    return request<ABTest[]>(`/ab-tests/${siteId}`);
}

export async function createABTest(siteId: string, data: Omit<ABTest, 'id' | 'created_at' | 'status' | 'stats' | 'site_id'>): Promise<ApiResponse<ABTest>> {
    return request(`/ab-tests/${siteId}`, {
        method: 'POST',
        body: JSON.stringify(data)
    });
}

export async function updateABTestStatus(siteId: string, testId: string, status: 'active' | 'paused' | 'stopped'): Promise<ApiResponse<any>> {
    return request(`/ab-tests/${siteId}/${testId}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
    });
}

// --- Backups ---
export interface SiteBackup {
    id: string;
    site_id: string;
    name: string;
    size_bytes: number;
    file_count: number;
    created_at: string;
}

export async function getBackups(siteId: string): Promise<ApiResponse<SiteBackup[]>> {
    return request<SiteBackup[]>(`/upload/backups/${siteId}`);
}

export async function createBackup(siteId: string, name: string): Promise<ApiResponse<SiteBackup>> {
    return request(`/upload/backup/${siteId}`, {
        method: 'POST',
        body: JSON.stringify({ name })
    });
}

export async function restoreBackup(siteId: string, backupId: string): Promise<ApiResponse<any>> {
    return request(`/upload/restore/${siteId}/${backupId}`, {
        method: 'POST'
    });
}

export async function deleteBackup(siteId: string, backupId: string): Promise<ApiResponse<any>> {
    return request(`/upload/backup/${siteId}/${backupId}`, {
        method: 'DELETE'
    });
}

// --- API Tokens ---
export interface ApiToken {
    id: string;
    name: string;
    token?: string; // Only returned on creation
    created_at: string;
    expires_at: string | null;
    last_used_at: string | null;
}

export async function createApiToken(name: string, expiresInDays?: number): Promise<ApiResponse<ApiToken>> {
    return request('/tokens', {
        method: 'POST',
        body: JSON.stringify({ name, expiresInDays })
    });
}

export async function getApiTokens(): Promise<ApiResponse<ApiToken[]>> {
    return request('/tokens');
}

export async function revokeApiToken(tokenId: string): Promise<ApiResponse<any>> {
    return request(`/tokens/${tokenId}`, {
        method: 'DELETE'
    });
}

// App Store
export interface BlueprintField {
    key: string;
    label: string;
    type: 'text' | 'color' | 'password' | 'url' | 'file' | 'textarea';
    placeholder?: string;
    required: boolean;
    group: 'general' | 'branding' | 'integrations' | 'ai';
}

export interface Blueprint {
    id: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    thumbnail: string;
    features: string[];
    fields: BlueprintField[];
}

export interface LaunchResult {
    siteId: string;
    domain: string;
    blueprint: string;
    fileCount: number;
    totalSize: number;
}

export async function getBlueprints(): Promise<ApiResponse<Blueprint[]>> {
    return request('/app-store/blueprints');
}

export async function launchApp(blueprintId: string, config: Record<string, string>): Promise<ApiResponse<LaunchResult>> {
    return request('/app-store/launch', {
        method: 'POST',
        body: JSON.stringify({ blueprintId, config })
    });
}

// --- General System Admin ---

export async function updateProfile(data: Partial<Pick<User, 'full_name' | 'company_name' | 'vat_number' | 'billing_address' | 'phone' | 'proof_of_address_url' | 'copy_of_id_url'>>): Promise<ApiResponse<{ user: Omit<User, 'password_hash'> }>> {
    return request('/auth/me', {
        method: 'PATCH',
        body: JSON.stringify(data)
    });
}

export async function uploadKycDocument(filename: string, file: File): Promise<ApiResponse<{ objectKey: string; url: string; }>> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}/upload/kyc/direct/${encodeURIComponent(filename)}`, {
        method: 'POST',
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: file
    });

    try {
        return await response.json() as any;
    } catch (err) {
        return { success: false, error: 'Server error saving file' } as any;
    }
}

// End of API
